import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import moment from "moment";
import { useStoreState } from "ezStore";

import { Button, Card, FormGroup, Input, Select, Table, Textarea } from "@cofano/cck";

import { Box, Divider, Grid, Step, StepLabel, Stepper, Tab, Tabs } from "@mui/material";
import Delete from "@mui/icons-material/Delete";

import { CustomDialog } from "components/dialogs/Dialogs";
import FormField from "components/cck/FormField";
import UserSelect from "components/selects/UserSelect";
import RelationSelect, { RelationSelectOption } from "view/relations/components/selects/RelationSelect";
import ContractSendHistory from "components/finance/ContractSendHistory";
import { Language, shortid } from "imports/Utils";
import { localeAsLanguageKey, locales } from "utils/locales";
import { FormattedDate } from "utils/DateUtils";
import { useLazyQuery } from "@apollo/client";
import { Contact, ContractStatusType, Document, User } from "__generated__/graphql";
import { useMutation } from "@apollo/client";
import { sendContractMail_mutation } from "view/contractsNew/utils/ContractQueries";
import { useQuery } from "@apollo/client";
import { GetApplicationRelationDocument, GetApplicationRelationQuery } from "__generated__/view/relations/graphql/relation.queries.generated";
import { LanguageSetByCodeDocument } from "__generated__/view/admin/graphql/admin.queries.generated";

type RelationFromQuery = GetApplicationRelationQuery["applicationRelation"];

type ContractRelation = RelationSelectOption | null | undefined;
type ContractContact = Pick<Contact, "id" | "name" | "email" | "telephone"> | null | undefined;

type SendContractDialogProps = {
	open: boolean;
	onClose: () => void;
	contract: {
		id: number;
		creditor?: ContractRelation;
		creditorContact?: ContractContact;
		debtor?: ContractRelation;
		debtorContact?: ContractContact;
		description?: string | null;
		documents?: (Pick<Document, "id" | "filename" | "description" | "created"> | null)[] | null;
		status: ContractStatusType;
		versionNumber?: string | number | null;
	};
	refetch: () => void;
};

type Recipient = {
	id: number | null;
	recipientUser?: User;
	recipientRelation?: RelationFromQuery;
	recipientContact?: Contact;
	recipientEmail?: string;
	languageCode: string;
};

enum ReceiverType {
	USER = "USER",
	RELATION = "RELATION",
	CONTACT_PERSON = "CONTACT_PERSON",
	EMAIL = "EMAIL"
}

const SendContractDialog = ({ open, onClose, contract, refetch }: SendContractDialogProps) => {

	const user = useStoreState(state => state.session.user);

	const  { data }  = useQuery<GetApplicationRelationQuery>(GetApplicationRelationDocument);
	const applicationRelation = data?.applicationRelation;

	const [ sendContractMail, { loading } ] = useMutation(sendContractMail_mutation);

	const [ activeStep, setActiveStep ] = useState<number>(0);
	const [ recipients, setRecipients ] = useState<Recipient[]>([]);
	const [ emailTexts, setEmailTexts ] = useState<Record<string, string>>({});
	const [ documents, selectDocuments ] = useState<Document[]>([]);

	const recipientsRef = useRef(recipients);

	const handleOnClose = () => {
		onClose();
		setActiveStep(0);
		setRecipients([]);
		setEmailTexts({});
		selectDocuments([]);
	};

	const send = () => {
		const emailTemplates = Object.entries(emailTexts).map(([ key, value ]) => ({
			languageKey: key,
			translation: value,
		}));

		sendContractMail({
			variables: {
				input: {
					contract: contract.id,
					emailTemplates: emailTemplates,
					recipients: recipients?.map(recipient => ({
						user: recipient.recipientUser?.id,
						relation: recipient.recipientRelation?.id,
						contact: recipient.recipientContact?.id,
						email: recipient.recipientEmail,
						languageCode: recipient.languageCode,
					})),
					documents: [ ...documents ].map(document => document.id),
				},
			},
		}).then(() => {
			refetch();
			handleOnClose();
		});
	};

	useEffect(() => {
		recipientsRef.current = recipients;
	}, [ recipients ]);

	useEffect(() => {
		if (applicationRelation && open) {
			setRecipients([ {
				id: shortid.generate(),
				recipientRelation: { ...applicationRelation },
				languageCode: applicationRelation.language?.code ?? "en",
			} ]);
		}
	}, [ applicationRelation, open ]);

	return (
		<CustomDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "SEND_CONTRACT" })}
			actions={(
				<React.Fragment>
					{activeStep === 0 && (
						<Button
							disabled={recipients.length === 0}
							onClick={() => setActiveStep(1)}
							size="small">
							{intl.formatMessage({ id: "NEXT_PAGE" })}
						</Button>
					)}

					{activeStep === 1 && (
						<React.Fragment>
							<Button	onClick={() => setActiveStep(0)} size="small">
								{intl.formatMessage({ id: "PREV" })}
							</Button>

							<Button	onClick={() => setActiveStep(2)} size="small">
								{intl.formatMessage({ id: "NEXT_PAGE" })}
							</Button>
						</React.Fragment>
					)}

					{activeStep === 2 && (
						<React.Fragment>
							<Button	onClick={() => setActiveStep(1)} size="small">
								{intl.formatMessage({ id: "PREV" })}
							</Button>

							<Button	onClick={() => setActiveStep(3)} size="small">
								{intl.formatMessage({ id: "NEXT_PAGE" })}
							</Button>
						</React.Fragment>
					)}

					{activeStep === 3 && (
						<React.Fragment>
							<Button	onClick={() => setActiveStep(2)} size="small">
								{intl.formatMessage({ id: "PREV" })}
							</Button>

							<Button
								onClick={() => send()}
								loading={loading}
								disabled={loading}
								size="small">
								{intl.formatMessage({ id: "SEND" })}
							</Button>
						</React.Fragment>
					)}
				</React.Fragment>
			)}
			maxWidth="md">
			<React.Fragment>
				<Box sx={{ borderBottom: 1, borderColor: "divider", marginBottom: 2, paddingBottom: 2 }}>
					<Stepper activeStep={activeStep}>
						<Step>
							<StepLabel>{intl.formatMessage({ id: "RECEIVERS" })}</StepLabel>
						</Step>

						<Step>
							<StepLabel>{intl.formatMessage({ id: "EMAIL" })}</StepLabel>
						</Step>

						<Step>
							<StepLabel>{intl.formatMessage({ id: "ATTACHMENTS" })}</StepLabel>
						</Step>

						<Step>
							<StepLabel>{intl.formatMessage({ id: "CONFIRM" })}</StepLabel>
						</Step>
					</Stepper>
				</Box>

				{activeStep === 0 && (
					<Grid container spacing={3}>
						<Grid item xs={6}>
							<ReceiverForm
								contract={contract}
								onAdd={(data) => {
									//@ts-expect-error
									setRecipients([ ...recipients, { ...data } ]);
								}} />
						</Grid>

						<Grid item xs={6}>
							<Table
								id="cckTableContractRecipients"
								disableFooter={true}
								//@ts-expect-error
								content={recipients}
								columns={[ {
									id: "receiver",
									title: intl.formatMessage({ id: "RECEIVER" }),
									render: row => {
										return row.recipientUser?.email || row.recipientRelation?.email || row.recipientContact?.email || row.recipientEmail || Language("UNKNOWN");
									},
								}, {
									id: "languageCode",
									title: intl.formatMessage({ id: "USER_LANGUAGE" }),
									render: (row) => localeAsLanguageKey(row.languageCode),
								} ]}
								rowActions={[ {
									icon: <Delete />,
									onClick: (row) => setRecipients(recipientsRef.current.filter(r => r.id !== row.id)),
								} ]} />
						</Grid>
					</Grid>
				)}

				{activeStep === 1 && (
					<Message
						recipients={recipients}
						applicationRelation={applicationRelation}
						contract={contract}
						emailTexts={emailTexts}
						setEmailTexts={setEmailTexts} />
				)}

				{activeStep === 2 && (
					<Table
						id="cckTableContractStepAttachments"
						disableFooter={true}
						content={contract.documents?.map(it => ({ ...it, id: it?.id ?? undefined })) ?? []}
						selectSettings={{
							//@ts-expect-error
							onSelect: (rows: Document[]) => selectDocuments(rows),
							isFullSelectEnabled: true,
							defaultSelection: documents,
						}}
						columns={[ {
							id: "filename",
							title: intl.formatMessage({ id: "FILE" }),
						}, {
							id: "description",
							title: intl.formatMessage({ id: "DESCRIPTION" }),
						}, {
							id: "created",
							title: intl.formatMessage({ id: "UPLOADED" }),
							render: row => <FormattedDate date={row.created} withoutYear />,
						} ]} />
				)}

				{activeStep === 3 && (
					<ContractSendHistory
						history={recipients.map(recipient => ({
							timestamp: moment.now(),
							status: contract.status,
							versionNumber: contract.versionNumber,
							sendBy: {
								id: user?.properties.id,
								username: user?.properties.username,
							},
							language: {
								code: recipient.languageCode,
							},
							recipientUser: recipient.recipientUser,
							recipientRelation: recipient.recipientRelation,
							recipientContact: recipient.recipientContact,
							recipientEmail: recipient.recipientEmail,
						}))} />
				)}
			</React.Fragment>
		</CustomDialog>
	);
};

type ReceiverFormProps = {
	id: string;
	type?: ReceiverType;
	recipientUser?: Pick<User, "id" | "username"> | undefined;
	recipientRelation?: RelationSelectOption;
	contactRelation?: RelationSelectOption;
	recipientContact?: Pick<Contact, "id" | "name" | "email"> | undefined;
	recipientEmail?: string;
	languageCode: string;
};

const ReceiverForm = ({ contract, onAdd }: {
	contract: {
		creditor?: ContractRelation;
		creditorContact?: ContractContact;
		debtor?: ContractRelation;
		debtorContact?: ContractContact;
	};
	onAdd: (item: ReceiverFormProps) => void;
}) => {

	const counterParty = (contract.creditor?.applicationRelation ? contract.debtor : contract.creditor) as (RelationSelectOption | undefined);
	const counterPartyContact = (contract.creditor?.applicationRelation ? contract.debtorContact : contract.creditorContact) as (RelationSelectOption | undefined);

	const defaultValues = {
		type: counterPartyContact ? ReceiverType.CONTACT_PERSON : ReceiverType.RELATION,
		recipientUser: undefined,
		recipientRelation: undefined,
		contactRelation: counterParty,
		recipientContact: undefined,
		recipientEmail: "",
		languageCode: locales.NL,
	};

	const { control, handleSubmit, formState, reset, watch } = useForm<ReceiverFormProps>({
		defaultValues: defaultValues,
	});

	const values = watch();
	const contacts = (values.contactRelation?.contacts || []).map(it => ({ ...it, disabled: !it.email }));

	const save = (data: ReceiverFormProps) => {
		if (data.type === ReceiverType.USER) {
			onAdd({
				id: shortid.generate(),
				recipientUser: data.recipientUser,
				languageCode: data.languageCode,
			});
		} else if (data.type === ReceiverType.RELATION) {
			onAdd({
				id: shortid.generate(),
				recipientRelation: data.recipientRelation,
				languageCode: data.languageCode,
			});
		} else if (data.type === ReceiverType.CONTACT_PERSON) {
			onAdd({
				id: shortid.generate(),
				recipientContact: data.recipientContact,
				languageCode: data.languageCode,
			});
		} else {
			onAdd({
				id: shortid.generate(),
				recipientEmail: data.recipientEmail,
				languageCode: data.languageCode,
			});
		}

		reset({
			...defaultValues,
			type: values.type,
		});
	};

	return (
		<form onSubmit={handleSubmit(save)}>
			<FormGroup label={Language("RECIPIENT_TYPE")} overflow="hidden">
				<Controller
					name="type"
					control={control}
					render={({ field }) => (
						<Select
							{...field}
							fetchOptions={Object.values(ReceiverType)}
							getOptionLabel={option => intl.formatMessage({ id: option })}
							clearable={false}
							disableSearch />
					)} />
			</FormGroup>

			{values.type === ReceiverType.USER && (
				<FormGroup label={Language("RECEIVER")} overflow="hidden" required>
					<FormField errors={formState.errors} name={"recipientUser"}>
						<Controller
							name="recipientUser"
							control={control}
							rules={{ required: true }}
							render={({ field }) => <UserSelect {...field} multiple={false} />} />
					</FormField>
				</FormGroup>
			)}

			{values.type === ReceiverType.RELATION && (
				<FormGroup label={Language("RECEIVER")} overflow="hidden" required>
					<FormField errors={formState.errors} name={"recipientRelation"}>
						<Controller
							name="recipientRelation"
							control={control}
							rules={{ required: true }}
							render={({ field }) => <RelationSelect {...field} />} />
					</FormField>
				</FormGroup>
			)}

			{values.type === ReceiverType.CONTACT_PERSON && (
				<React.Fragment>
					<FormGroup label={Language("RELATION")} overflow="hidden" required>
						<FormField errors={formState.errors} name={"contactRelation"}>
							<Controller
								name="contactRelation"
								control={control}
								rules={{ required: true }}
								render={({ field }) => <RelationSelect {...field} />} />
						</FormField>
					</FormGroup>

					<FormGroup label={Language("RECEIVER")} overflow="hidden" required>
						<FormField errors={formState.errors} name={"recipientContact"}>
							<Controller
								name="recipientContact"
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<Select
										{...field}
										fetchOptions={contacts}
										renderOption={(props, option: Contact) => (
											<li {...props}>
												<div>
													<div>{option.name}</div>
													{!option.email && (
														<div>
															<small>{intl.formatMessage({ id: "RECIPIENT_NO_EMAIL" })}</small>
														</div>
													)}
												</div>
											</li>
										)} />
								)} />
						</FormField>
					</FormGroup>
				</React.Fragment>
			)}

			{values.type === ReceiverType.EMAIL && (
				<FormGroup label={Language("RECEIVER")} overflow="hidden" required>
					<FormField errors={formState.errors} name={"recipientEmail"}>
						<Controller
							name="recipientEmail"
							control={control}
							rules={{ required: true }}
							render={({ field }) => <Input {...field} type="email" />} />
					</FormField>
				</FormGroup>
			)}

			<FormGroup label={Language("USER_LANGUAGE")} overflow="hidden">
				<Controller
					name="languageCode"
					control={control}
					render={({ field }) => (
						<Select
							{...field}
							fetchOptions={Object.values(locales)}
							getOptionLabel={option => intl.formatMessage({ id: intl.formatMessage({ id: `LANGUAGE_${option.toUpperCase()}` }) })}
							clearable={false}
							disableSearch />
					)} />
			</FormGroup>

			<div style={{ marginTop: 16 }}>
				<Button type="submit" variant="outlined" size="small">
					{Language("ADD_BUTTON")}
				</Button>
			</div>
		</form>
	);
};

type MessageProps = {
	recipients: Recipient[];
	applicationRelation?: RelationFromQuery;
	contract: {
		description?: string | null;
		creditorContact?: ContractContact;
	};
	emailTexts: Record<string, string>;
	setEmailTexts: (text: Record<string, string>) => void;
};

const Message = ({ recipients, applicationRelation, contract, emailTexts, setEmailTexts }: MessageProps) => {

	const user = useStoreState(state => state.session.user);

	const getLanguageCodes = () => {
		const removeDuplicates = (arr: string[]) => arr.filter((item: string, index: number) => arr.indexOf(item) === index);
		return removeDuplicates([ ...recipients ].map(it => it.languageCode.toUpperCase()));
	};

	const languageCodes = getLanguageCodes();

	const [ selectedTab, selectTab ] = useState(languageCodes[0]);

	const [ getLanguageSetByCode, { loading } ] = useLazyQuery(LanguageSetByCodeDocument);

	useEffect(() => {
		const codes = [ ...languageCodes ].filter(code => !Object.keys(emailTexts).includes(code));

		if (codes.length > 0) {
			Promise.all(codes.map((code: string) => getLanguageSetByCode({
				variables: {
					code: code,
				},
			}))).then(responses => {

				const texts: Record<string, string> = {
					...emailTexts,
				};

				responses.forEach((result, index) => {
					const langCode = codes[index];
					const pairs = result.data?.languageSetByCode ?? [];

					const contractEmailEntry = pairs.find(
						entry => entry?.key === "CONTRACT_EMAIL",
					);

					if (contractEmailEntry?.translation) {
						texts[langCode] = contractEmailEntry.translation;
					}
				});
				setEmailTexts(texts);
			});
		}
	}, []);

	return (
		<div>
			<Box sx={{ borderBottom: 1, borderColor: "divider", marginBottom: 3 }}>
				<Tabs
					value={selectedTab}
					onChange={(e, value) => selectTab(value)}
					indicatorColor="primary">
					{languageCodes.map((it: string, index: number) => (
						<Tab value={it} label={it} key={index} />
					))}
				</Tabs>
			</Box>

			<Grid spacing={3} container>
				<Grid xs={6} item>
					<Textarea
						rows={15}
						value={emailTexts[selectedTab]}
						disabled={loading}
						onChange={(e) => {
							const newEmailTexts = { ...emailTexts, [selectedTab]: e.target.value };
							setEmailTexts(newEmailTexts);
						}} />
				</Grid>

				<Grid xs={6} item>
					<Card title={Language("VARIABLES")}>
						<div>
							<FormGroup label="_MY_COMPANY_"
								labelPlacement="left-center"
								labelWidth={220}
								overflow="hidden">
								{applicationRelation?.name}
							</FormGroup>

							<FormGroup label="_MY_COMPANY.EMAIL_"
								labelPlacement="left-center"
								labelWidth={220}
								overflow="hidden">
								{applicationRelation?.email}
							</FormGroup>

							<Divider style={{ marginBottom: 8 }} />

							<FormGroup label="_SENDER_NAME_"
								labelPlacement="left-center"
								labelWidth={220}
								overflow="hidden">
								{user?.displayName}
							</FormGroup>

							<FormGroup label="_SENDER_EMAIL_"
								labelPlacement="left-center"
								labelWidth={220}
								overflow="hidden">
								{user?.properties.email}
							</FormGroup>

							<FormGroup label="_SENDER_PHONE_"
								labelPlacement="left-center"
								labelWidth={220}
								overflow="hidden">
								{user?.properties.phone}
							</FormGroup>

							<Divider style={{ marginBottom: 8 }} />

							<FormGroup label="_SENDER_CONTACT_NAME_"
								labelPlacement="left-center"
								labelWidth={220}
								overflow="hidden">
								{contract.creditorContact?.name}
							</FormGroup>

							<FormGroup label="_SENDER_CONTACT_EMAIL_"
								labelPlacement="left-center"
								labelWidth={220}
								overflow="hidden">
								{contract.creditorContact?.email}
							</FormGroup>

							<FormGroup label="_SENDER_CONTACT_PHONE_"
								labelPlacement="left-center"
								labelWidth={220}
								overflow="hidden">
								{contract.creditorContact?.telephone}
							</FormGroup>

							<Divider style={{ marginBottom: 8 }} />

							<FormGroup label="_CONTRACT.DESCRIPTION_"
								labelPlacement="left-center"
								labelWidth={220}
								overflow="hidden">
								{contract.description}
							</FormGroup>
						</div>
					</Card>
				</Grid>
			</Grid>
		</div>
	);
};

export default SendContractDialog;
